import type { NodeSpec } from 'prosemirror-model'
import type TurndownService from 'turndown'
import type { MarkedExtension, Token, Tokens } from 'marked'
import { Lexer, Marked } from 'marked'

export const kPleadingCaptionNodeName = 'pleading_caption'
export const kPleadingCaptionBlockName = 'pleading-caption'

const kPleadingCaptionTokenName = 'pleadingCaption'
const kPleadingCaptionFence = ':::'
const pleadingCaptionOpeningLinePattern = `^${kPleadingCaptionFence}${kPleadingCaptionBlockName}[ \\t]*`
const pleadingCaptionStartPattern = new RegExp(`${pleadingCaptionOpeningLinePattern}$`, 'm')
const pleadingCaptionBlockPattern = new RegExp(`${pleadingCaptionOpeningLinePattern}\\r?\\n([\\s\\S]*?)\\r?\\n${kPleadingCaptionFence}[ \\t]*(?:\\r?\\n|$)`)

interface PleadingCaptionToken extends Tokens.Generic {
  type: typeof kPleadingCaptionTokenName
  tokens: Token[]
}

/**
 * Parse a supported pleading-caption directive into a dedicated wrapper around
 * one GFM table. The inert HTML wrapper preserves the block's identity through
 * sanitization and ProseMirror parsing. Malformed directives remain ordinary Markdown.
 */
const pleadingCaptionMarkedExtension: MarkedExtension = {
  extensions: [
    {
      name: kPleadingCaptionTokenName,
      level: 'block',
      start(source) {
        return source.match(pleadingCaptionStartPattern)?.index
      },
      tokenizer(source) {
        const match = pleadingCaptionBlockPattern.exec(source)
        if (!match) return undefined

        // Check the body with a separate lexer first: a rejected attempt on the shared lexer leaves
        // queued inline work that marked later fills with the text of the paragraph above. The
        // separate lexer gets its own options, because sharing them would rebind the shared tokenizer.
        const candidate = new Lexer({ gfm: true }).blockTokens(match[1].trim())
        if (candidate.length !== 1 || candidate[0].type !== 'table') return undefined

        const tokens = this.lexer.blockTokens(match[1].trim())

        return {
          type: kPleadingCaptionTokenName,
          raw: match[0],
          tokens,
        } satisfies PleadingCaptionToken
      },
      renderer(token) {
        const pleadingCaptionToken = token as PleadingCaptionToken
        return `<${kPleadingCaptionBlockName}>${this.parser.parse(pleadingCaptionToken.tokens)}</${kPleadingCaptionBlockName}>`
      },
      childTokens: ['tokens'],
    },
  ],
}

/** The artifact editor's Markdown parser: GFM plus the caption block. Lives here because editor-helpers imports this module. */
export const artifactMarkdownParser = new Marked({ gfm: true }, pleadingCaptionMarkedExtension)

/** Show supported captions as ordinary tables in read-only displays; stored Markdown keeps its fences. */
export function unwrapPleadingCaptionsForDisplay(markdown: string): string {
  const tokens = artifactMarkdownParser.lexer(markdown)
  if (!tokens.some((token) => token.type === kPleadingCaptionTokenName)) return markdown
  return tokens.map((token) => (token.type === kPleadingCaptionTokenName ? token.raw.replace(pleadingCaptionBlockPattern, '\n$1\n\n') : token.raw)).join('')
}

/** Expand an AI edit target to include every caption it touches, without changing the visible highlight. */
export function expandPleadingCaptionMarkdownRange(markdown: string, startUtf16: number, endUtf16: number): { startUtf16: number; endUtf16: number } {
  let offset = 0
  for (const token of artifactMarkdownParser.lexer(markdown)) {
    const blockEnd = offset + token.raw.trimEnd().length
    if (token.type === kPleadingCaptionTokenName && startUtf16 < blockEnd && endUtf16 > offset) {
      startUtf16 = Math.min(startUtf16, offset)
      endUtf16 = Math.max(endUtf16, blockEnd)
    }
    offset += token.raw.length
  }
  return { startUtf16, endUtf16 }
}

/** ProseMirror wrapper that keeps the custom block type around its editable table. */
export const pleadingCaptionNodeSpec: NodeSpec = {
  group: 'block',
  content: 'table',
  defining: true,
  isolating: true,
  parseDOM: [{ tag: kPleadingCaptionBlockName }],
  toDOM() {
    return [kPleadingCaptionBlockName, 0]
  },
}

/** Serialize the ProseMirror wrapper back to the same custom Markdown directive. */
export function addPleadingCaptionTurndownRule(service: TurndownService): void {
  service.addRule(kPleadingCaptionTokenName, {
    filter: (node) => node.nodeName.toLowerCase() === kPleadingCaptionBlockName,
    replacement: (content) => {
      const table = content.trim()
      // The GFM plugin writes a table as HTML when a cell holds a heading, list, etc. A fence around
      // HTML would reload as visible text, so keep it as the same ordinary table a plain table becomes.
      if (!table.startsWith('|')) return `\n\n${table}\n\n`
      return `\n\n${kPleadingCaptionFence}${kPleadingCaptionBlockName}\n${table}\n${kPleadingCaptionFence}\n\n`
    },
  })
}
